import {
  DraftOrdersListResponseType,
  UnifiedCheckoutBasketType,
} from "@sellar-io/api-v2/types";
import { formatCurrency, pluralize, truncate } from "@sellar-io/common";
import {
  Button,
  Card,
  H5,
  H6,
  Icon,
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
} from "@sellar-io/components";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { SpinnerLegacy, ProfilePicture } from "../../../../components";
import { setFallbackImage } from "../../../../components/image/imageWithFallback.shared";
import {
  DRAFT_ORDERS,
  DISCOVER_SEARCH,
  ORDER_BUYER,
  UNIFIED_CHECKOUT_BASKET,
} from "../../../../constants/routes";
import {
  BasketContextActionEnum,
  BasketOrderItem,
  ContextOrderBasketType,
  useBasketContext,
} from "../../../../hooks";
import { isPromotionalOrderItem } from "../../../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { useDraftDbsBaskets } from "./useDraftDbsBaskets";
import { useCartSummary } from "./useCartSummary";
import { useUnifiedCheckoutBasketUpdateItem } from "../../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useNavigateToChild } from "../../../../hooks/useParentNavigation";
import { InfoBadge } from "~/components/badge/InfoBadge";
import { TrackedCutoffCountdown } from "./CutoffCountdown/TrackedCutoffCountdown";
import { DeliveredBySellarPrompt } from "./DeliveredBySellarPrompt";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { useShallow } from "zustand/react/shallow";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { getConsolidatedBasketIntercomData } from "./consolidatedOrderingTracking";
import { useConsolidatedOrderingIntercom } from "../../../../hooks/useConsolidatedOrderingIntercom";
import { calculateUnifiedCheckoutOrderPricing } from "../../../unifiedCheckout/lib/calculateUnifiedCheckoutOrderPricing";
import { getIncompleteUnifiedCheckoutCaseSizeRules } from "../../../unifiedCheckout/lib/getIncompleteUnifiedCheckoutCaseSizeRules";
import { UnifiedCheckoutCaseSizeRuleStatus } from "../../../unifiedCheckout/components/UnifiedCheckoutCaseSizeRuleStatus";

const BASKETS_TAB = 0;

// ─── Qty stepper (no StockListContext dependency) ─────────────────────────────

const QtyControl = ({
  item,
  order,
  onOrderChange,
  disabled,
}: {
  item: BasketOrderItem;
  order: ContextOrderBasketType;
  onOrderChange: (_updated: ContextOrderBasketType) => void;
  disabled?: boolean;
}) => {
  const change = (delta: number) => {
    const newQty = item.quantity + delta;
    const updated: ContextOrderBasketType = {
      ...order,
      OrderItems:
        newQty <= 0
          ? (order.OrderItems?.filter(
              (i) => i.Product.id !== item.Product.id,
            ) ?? [])
          : (order.OrderItems?.map((i) =>
              i.Product.id === item.Product.id ? { ...i, quantity: newQty } : i,
            ) ?? []),
    };
    onOrderChange(updated);
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          change(-1);
        }}
        aria-label="Decrease quantity"
        disabled={disabled}
        type="button"
        className="flex-none shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
      >
        <Icon icon="MINUS" size={0.6} />
      </Button>
      <span className="min-w-6 flex-1 rounded-lg border border-zinc-300 bg-white px-3 py-1 text-center text-sm font-semibold text-zinc-800">
        {item.quantity}
      </span>
      <Button
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          change(1);
        }}
        aria-label="Increase quantity"
        disabled={disabled}
        type="button"
        className="flex-none shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]"
      >
        <Icon icon="PLUS" size={0.6} />
      </Button>
    </div>
  );
};

// ─── Single line-item row ─────────────────────────────────────────────────────

const LineItem = ({
  item,
  order,
  onOrderChange,
  onProductClick,
  disabled,
}: {
  item: BasketOrderItem;
  order: ContextOrderBasketType;
  onOrderChange: (_updated: ContextOrderBasketType) => void;
  onProductClick: () => void;
  disabled?: boolean;
}) => {
  const product = item.Product;
  const [parentName, variantName] = (product.name ?? "")
    .split("•")
    .map((s) => s.trim());

  const heroImageUrl = setFallbackImage({
    img: product.Parent?.heroImageUrl ?? product.heroImageUrl ?? null,
    fallback: "product-hero",
  });

  return (
    <div
      className="flex cursor-pointer items-center gap-3"
      onClick={onProductClick}
    >
      {/* Product thumbnail */}
      <div
        className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-zinc-200 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroImageUrl})` }}
      />

      {/* Name */}
      <div className="flex flex-1 flex-col gap-0.5">
        <H5 className="text-sm leading-tight font-semibold text-zinc-800">
          {truncate(parentName ?? "")}
        </H5>
        {variantName && (
          <span className="text-xs text-zinc-400">{variantName}</span>
        )}
      </div>

      {/* Price + qty stepper stacked */}
      <div className="flex flex-col items-end gap-1.5">
        <span className="text-sm font-semibold text-zinc-800">
          {formatCurrency(item.price, order.currency)}
        </span>
        <QtyControl
          item={item}
          order={order}
          onOrderChange={onOrderChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

// ─── Consolidated SD basket card ─────────────────────────────────────────────

type SdBasketOrderEntry = {
  order: ContextOrderBasketType;
  supplierStorefrontInfo: DraftOrdersListResponseType[number];
};

const ConsolidatedSdBasketCard = ({
  index,
  sdBasketId,
  basket,
  entries,
  onClose,
  isDeliveredBySellarEnabled,
  onTurnOnDeliveredBySellar,
  onProductClick,
  onSupplierClick,
}: {
  index: number;
  sdBasketId: string;
  basket: UnifiedCheckoutBasketType;
  entries: SdBasketOrderEntry[];
  onClose: () => void;
  isDeliveredBySellarEnabled: boolean;
  onTurnOnDeliveredBySellar: () => void;
  onProductClick: (item: BasketOrderItem) => void;
  onSupplierClick: (supplier: { id: number; name: string }) => void;
}) => {
  const navigate = useNavigate();
  const [, dispatchBasket] = useBasketContext();
  const [deliveryInfoExpanded, setDeliveryInfoExpanded] = useState(false);

  const updateItemMutation = useUnifiedCheckoutBasketUpdateItem();
  const { trackBasketUpdated } = useConsolidatedOrderingIntercom();
  const session = useMarketStorefrontSessionId();

  const deliveryDateFormatted = basket.requestedDeliveryAt
    ? dayjs(basket.requestedDeliveryAt).format("ddd D MMM")
    : null;
  const basketLabel = (() => {
    if (basket.window === "weekend") return "Get it by the weekend";
    if (
      basket.requestedDeliveryAt &&
      dayjs(basket.requestedDeliveryAt).isAfter(
        dayjs().add(1, "week").endOf("week"),
      )
    ) {
      return "Week after next";
    }
    return "Next week";
  })();

  // Find the earliest cutoff across all orders in this basket
  const earliestCutoff = entries
    .map((e) => e.order.orderCutoffDate)
    .filter((d): d is NonNullable<typeof d> => !!d)
    .sort()[0];

  const cutoffUrgency = earliestCutoff
    ? dayjs(earliestCutoff).diff(dayjs(), "minute", true) <= 60
      ? "imminent"
      : dayjs(earliestCutoff).isSame(dayjs(), "day")
        ? "today"
        : "later"
    : "none";

  const showCutOff =
    earliestCutoff && ["imminent", "today"].includes(cutoffUrgency);

  // Build a map of local order state per supplier so in-flight edits don't clobber
  const [orderMap, setOrderMap] = useState<
    Record<number, ContextOrderBasketType>
  >(() =>
    Object.fromEntries(entries.map((e) => [e.order.SupplierId!, e.order])),
  );

  // Sync when entries change (e.g. order added/removed externally)
  const prevEntriesRef = useRef(entries);
  useEffect(() => {
    if (prevEntriesRef.current !== entries) {
      prevEntriesRef.current = entries;
      setOrderMap(
        Object.fromEntries(entries.map((e) => [e.order.SupplierId!, e.order])),
      );
    }
  }, [entries]);

  // Preserve supplier insertion order, then product insertion order within it.
  const supplierGroups = entries.map((entry) => {
    const order = orderMap[entry.order.SupplierId!] ?? entry.order;
    return {
      supplier: {
        id: entry.supplierStorefrontInfo.supplier.id,
        name: entry.supplierStorefrontInfo.supplier.Business.name,
      },
      order,
      items:
        order.OrderItems?.filter((item) => !isPromotionalOrderItem(item)).sort(
          (a, b) => (a.id ?? a.ProductId) - (b.id ?? b.ProductId),
        ) ?? [],
    };
  });

  const allItems = supplierGroups.flatMap(({ items, order, supplier }) =>
    items.map((item) => ({
      item,
      order,
      supplierName: supplier.name,
    })),
  );

  const totalItemCount = allItems.reduce(
    (acc, { item }) => acc + item.quantity,
    0,
  );

  const pricing = supplierGroups.map(({ order, items, supplier }) =>
    calculateUnifiedCheckoutOrderPricing({
      orderItems: items,
      storefront: entries.find(
        (entry) => entry.supplierStorefrontInfo.supplier.id === supplier.id,
      )?.supplierStorefrontInfo.storefront,
      vatPercentage: order.vatPercentage ?? 0,
    }),
  );
  const caseSizeRuleViolations = supplierGroups.flatMap(
    ({ order, supplier }) => {
      const supplierInfo = entries.find(
        (entry) => entry.supplierStorefrontInfo.supplier.id === supplier.id,
      );
      const rules = getIncompleteUnifiedCheckoutCaseSizeRules({
        orderItems: order.OrderItems ?? [],
        storefront: supplierInfo?.supplierStorefrontInfo.storefront,
      });
      return rules.length > 0
        ? [{ supplierId: supplier.id, supplierName: supplier.name, rules }]
        : [];
    },
  );
  const subTotal = pricing.reduce((sum, order) => sum + order.subtotal, 0);
  const discount = pricing.reduce((sum, order) => sum + order.discount, 0);
  const vatTotal = pricing.reduce((sum, order) => sum + order.vat, 0);
  const total = pricing.reduce((sum, order) => sum + order.total, 0);
  const vatPercentages = new Set(
    supplierGroups.map(({ order }) => order.vatPercentage ?? 0),
  );
  const vatLabel =
    vatPercentages.size === 1 ? `VAT (${[...vatPercentages][0]}%)` : "VAT";

  // Use first order's currency (all SD orders should share the same currency)
  const currency = entries[0]?.order.currency;

  const handleOrderChange = (
    supplierId: number,
    updated: ContextOrderBasketType,
  ) => {
    setOrderMap((prev) => ({ ...prev, [supplierId]: updated }));
    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: updated,
    });
    const previous = orderMap[supplierId];
    const changed =
      updated.OrderItems?.find((item) => {
        const oldItem = previous?.OrderItems?.find(
          (entry) => entry.ProductId === item.ProductId,
        );
        return oldItem?.quantity !== item.quantity;
      }) ??
      previous?.OrderItems?.find(
        (item) =>
          !updated.OrderItems?.some(
            (updatedItem) => updatedItem.ProductId === item.ProductId,
          ),
      );
    const backendItem = basket.DraftOrders.flatMap((draft) => draft.Items).find(
      (item) => item.ProductId === changed?.ProductId,
    );
    if (changed && backendItem) {
      updateItemMutation
        .mutateAsync({
          basketUuid: sdBasketId,
          itemId: backendItem.id,
          quantity:
            updated.OrderItems?.find(
              (item) => item.ProductId === changed.ProductId,
            )?.quantity ?? 0,
        })
        .then((updatedBasket) => {
          if (!updatedBasket) return;
          trackBasketUpdated({
            journeyKey: updatedBasket.uuid,
            ...getConsolidatedBasketIntercomData(updatedBasket, session),
          });
        });
    }
  };

  const handleCheckout = async () => {
    try {
      for (const entry of entries) {
        const order = orderMap[entry.order.SupplierId!] ?? entry.order;
        const nonPromoItems =
          order.OrderItems?.filter((item) => !isPromotionalOrderItem(item)) ??
          [];
        const prepared = { ...order, OrderItems: nonPromoItems };
        dispatchBasket({
          type: BasketContextActionEnum.CHANGE_ORDER,
          order: prepared,
        });
      }
      onClose();
      navigate(UNIFIED_CHECKOUT_BASKET.href({ sdBasketUuid: sdBasketId }));
    } catch (err) {
      console.error("Checkout failed", err);
    }
  };

  return (
    <Card className="overflow-hidden rounded-xl border border-zinc-300 bg-white p-0">
      <div className="overflow-hidden border-b border-zinc-300">
        <button
          type="button"
          className="flex w-full items-center gap-2 bg-white px-3 py-3 text-left"
          aria-expanded={deliveryInfoExpanded}
          onClick={() => setDeliveryInfoExpanded((expanded) => !expanded)}
        >
          <Icon
            icon="CALENDAR_OUTLINE"
            size={0.7}
            className="text-purple-600"
          />
          <H5 className="text-xl font-semibold text-zinc-800">
            {deliveryDateFormatted ?? `Basket ${index}`}
          </H5>
          <Icon
            icon={deliveryInfoExpanded ? "CHEVRON_DOWN" : "CHEVRON_RIGHT"}
            size={0.8}
            className="text-zinc-500"
          />
          <span className="ml-auto rounded-full bg-purple-600 px-2.5 py-1 text-xs font-semibold text-white">
            {basketLabel}
          </span>
        </button>

        {deliveryInfoExpanded && (
          <div className="w-full px-3 pb-3">
            <div className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-100 p-2 text-xs text-blue-800">
              <Icon icon="INFORMATION_OUTLINE" size={0.6} />
              <p>
                This is the earliest delivery date for <b>all items</b> in your
                basket. You can choose to split your delivery at checkout for
                faster delivery.
              </p>
            </div>
          </div>
        )}

        {showCutOff && (
          <div className="w-full px-3 pb-3">
            <div
              className={`flex items-center gap-2 rounded-lg p-2 text-xs ${
                cutoffUrgency === "imminent"
                  ? "bg-[#E8D3F4A8] text-[#8C23C7]"
                  : cutoffUrgency === "today"
                    ? "bg-[#E8D3F454] text-[#8C23C7]"
                    : "bg-[#E8D3F454] text-[#8C23C7]"
              }`}
            >
              <Icon icon="CLOCK_OUTLINE" size={0.65} />
              <span>
                {cutoffUrgency === "imminent" && (
                  <>
                    <span className="font-semibold">Cut-off soon!</span>{" "}
                  </>
                )}
                {cutoffUrgency === "imminent" ? "Order by" : "Order cut-off"}{" "}
                <span className="font-semibold">
                  {dayjs(earliestCutoff).format("h.mma")}

                  {cutoffUrgency === "today" ? " today" : ""}
                </span>
              </span>
              <TrackedCutoffCountdown
                cutoff={earliestCutoff}
                className="ml-auto font-semibold whitespace-nowrap"
              />
              {cutoffUrgency === "imminent" && (
                <Icon icon="INFORMATION" size={0.55} className="shrink-0" />
              )}
            </div>
          </div>
        )}
      </div>

      <span className="sr-only">
        {totalItemCount} {pluralize("item", totalItemCount)}
      </span>

      {!isDeliveredBySellarEnabled && (
        <DeliveredBySellarPrompt
          hasItems
          onTurnOn={onTurnOnDeliveredBySellar}
          className="m-3"
        />
      )}

      {/* ── Line items grouped by supplier ── */}
      <div className="flex flex-col gap-6 px-4 pt-3 pb-6">
        {supplierGroups.map(({ supplier, order, items }) => (
          <section key={supplier.id} className="flex flex-col gap-4">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between rounded-md bg-zinc-100 px-3 py-2 text-left text-sm font-semibold text-zinc-700 uppercase hover:bg-zinc-200"
              onClick={() => onSupplierClick(supplier)}
            >
              {supplier.name}
              <span className="flex items-center gap-1 font-normal normal-case">
                Shop <Icon icon="CHEVRON_RIGHT" size={0.65} />
              </span>
            </button>
            <div className="relative ml-1 flex flex-col gap-4 pl-5">
              {items.map((item) => (
                <LineItem
                  key={`${order.SupplierId}-${item.Product.id}`}
                  item={item}
                  order={order}
                  disabled={updateItemMutation.isPending}
                  onProductClick={() => onProductClick(item)}
                  onOrderChange={(updated) =>
                    handleOrderChange(order.SupplierId!, updated)
                  }
                />
              ))}
              <span className="absolute top-0 left-0 h-full w-1 rounded-lg bg-[#E8D3F4]" />
            </div>
          </section>
        ))}
      </div>

      {/* ── Totals ── */}
      <div className="flex flex-col gap-1 border-t border-zinc-300 bg-zinc-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <H6 className="text-sm text-zinc-800">Subtotal</H6>
          <H5 className="text-sm font-normal">
            {formatCurrency(subTotal, currency)}
          </H5>
        </div>
        <div className="flex items-center justify-between">
          <H6 className="text-sm text-zinc-800">Delivery</H6>
          <H5 className="text-sm font-medium text-violet-600">FREE</H5>
        </div>
        {discount > 0 && (
          <div className="flex items-center justify-between text-green-700">
            <H6 className="text-sm">Volume discount</H6>
            <H5 className="text-sm font-normal">
              -{formatCurrency(discount, currency)}
            </H5>
          </div>
        )}
        <div className="flex items-center justify-between">
          <H6 className="text-sm text-zinc-800">{vatLabel}</H6>
          <H5 className="text-sm font-normal">
            {formatCurrency(vatTotal, currency)}
          </H5>
        </div>
        <div className="mt-1 flex items-center justify-between border-t border-zinc-300 pt-2">
          <H6 className="text-sm font-medium text-zinc-800">Total</H6>
          <H5 className="text-lg font-semibold">
            {formatCurrency(total, currency)}
          </H5>
        </div>
      </div>

      {/* ── CTAs ── */}
      <div className="flex flex-col gap-2 px-4 pb-4">
        {caseSizeRuleViolations.map((violation) => (
          <UnifiedCheckoutCaseSizeRuleStatus
            key={violation.supplierId}
            supplierName={violation.supplierName}
            rules={violation.rules}
          />
        ))}
        <Button
          className="w-full"
          color="primary"
          disabled={caseSizeRuleViolations.length > 0}
          onClick={handleCheckout}
        >
          <Icon icon="LIGHTNING_BOLT" size={0.6} />
          Checkout all &bull; {formatCurrency(total, currency)}
        </Button>
      </div>
    </Card>
  );
};

// ─── Storefront summary card (read-only) ─────────────────────────────────────────

const StorefrontDraftOrderCard = ({
  order,
  supplierStorefrontInfo,
  onClose,
}: {
  order: ContextOrderBasketType;
  supplierStorefrontInfo: DraftOrdersListResponseType[number];
  onClose: () => void;
}) => {
  const navigateToChild = useNavigateToChild();
  const supplier = supplierStorefrontInfo.supplier;

  const itemCount =
    order.OrderItems?.reduce((acc, i) => acc + i.quantity, 0) ?? 0;

  const handleGoToStorefront = () => {
    onClose();
    navigateToChild(ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }));
  };

  return (
    <Card className="overflow-hidden border border-zinc-300 bg-white p-0">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-100">
          <ProfilePicture
            size="small"
            logoUrl={supplier.Business.logoUrl}
            fallback="supplier-badge"
          />
        </div>
        <div className="flex flex-1 flex-col">
          <H5 className="text-sm font-semibold text-zinc-800">
            {supplier.Business.name}
          </H5>
          <H6 className="text-xs text-zinc-500">
            {itemCount} {pluralize("item", itemCount)}
          </H6>
        </div>
        <Button color="secondary" size="small" onClick={handleGoToStorefront}>
          Go to storefront
        </Button>
      </div>
    </Card>
  );
};

// ─── Drawer shell ─────────────────────────────────────────────────────────────

/** Group SD draft orders by their sdBasketId into consolidated entries */
const buildSdBasketGroups = (
  dbsDraftOrders: ContextOrderBasketType[],
  supplierStorefrontPages: DraftOrdersListResponseType,
): Map<string, SdBasketOrderEntry[]> => {
  const groups = new Map<string, SdBasketOrderEntry[]>();

  for (const order of dbsDraftOrders) {
    const basketId = order.sdBasketUuid;
    if (basketId == null) continue;

    const supplierInfo = supplierStorefrontPages.find(
      (info) => info.supplier.id === order.SupplierId,
    );
    if (!supplierInfo) continue;

    const existing = groups.get(basketId) ?? [];
    existing.push({ order, supplierStorefrontInfo: supplierInfo });
    groups.set(basketId, existing);
  }

  return groups;
};

export const DraftBasketDrawer = ({
  active,
  setActive,
  showDeliveredBySellarFeatures = true,
  includeStorefrontDrafts = true,
  appear = false,
}: {
  active: boolean;
  setActive: (_value: boolean) => void;
  showDeliveredBySellarFeatures?: boolean;
  includeStorefrontDrafts?: boolean;
  appear?: boolean;
}) => {
  const {
    dbsDraftOrders,
    storefrontDraftOrders,
    draftOrderListQuery,
    baskets,
    isLoading,
  } = useDraftDbsBaskets({ includeStorefrontDrafts });
  const { totalItemCount } = useCartSummary({
    showDeliveredBySellarFeatures,
    includeStorefrontDrafts,
  });
  const {
    isDeliveredBySellar,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setSelectedProductIds,
    setSelectedSuppliers,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      isDeliveredBySellar: state.isDeliveredBySellar,
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setSelectedProductIds: state.setSelectedProductIds,
      setSelectedSuppliers: state.setSelectedSuppliers,
    })),
  );

  const navigate = useNavigate();
  const turnOnDeliveredBySellar = () => {
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("all");
  };
  const continueDeliveredBySellarShopping = () => {
    turnOnDeliveredBySellar();
    setActive(false);
    navigate(`${DISCOVER_SEARCH}?isDeliveredBySellar=true`);
  };
  const openProduct = (item: BasketOrderItem) => {
    setActive(false);
    setSelectedProductIds({
      productItemId: item.ProductId,
      parentProductId:
        item.Product.Parent?.id ?? item.Product.ParentId ?? undefined,
    });
    navigate(DISCOVER_SEARCH);
  };
  const openSupplier = (supplier: { id: number; name: string }) => {
    setActive(false);
    setSelectedProductIds(undefined);
    setSelectedSuppliers([supplier]);
    navigate(DISCOVER_SEARCH);
  };

  useEffect(() => {
    if (showDeliveredBySellarFeatures && active && dbsDraftOrders.length > 0) {
      setIsDeliveredBySellar(true);
      setDeliveredBySellarOption("all");
    }
  }, [
    active,
    dbsDraftOrders.length,
    showDeliveredBySellarFeatures,
    setDeliveredBySellarOption,
    setIsDeliveredBySellar,
  ]);

  const supplierStorefrontPages = draftOrderListQuery.data?.pages.flat() ?? [];
  const sdBasketGroups = buildSdBasketGroups(
    dbsDraftOrders,
    supplierStorefrontPages,
  );
  const sortedSdBasketGroups = [...sdBasketGroups.entries()].sort(
    ([firstBasketId], [secondBasketId]) => {
      const firstDeliveryDate = baskets.find(
        (basket) => basket.uuid === firstBasketId,
      )?.requestedDeliveryAt;
      const secondDeliveryDate = baskets.find(
        (basket) => basket.uuid === secondBasketId,
      )?.requestedDeliveryAt;

      if (!firstDeliveryDate) return secondDeliveryDate ? 1 : 0;
      if (!secondDeliveryDate) return -1;

      return (
        dayjs(firstDeliveryDate).valueOf() - dayjs(secondDeliveryDate).valueOf()
      );
    },
  );
  return (
    <TabbedDrawer
      appear={appear}
      active={active}
      setActive={setActive}
      initTab={BASKETS_TAB}
      closeIcon="CHEVRON_DOUBLE_RIGHT"
    >
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={
            showDeliveredBySellarFeatures || !includeStorefrontDrafts
              ? "Basket"
              : "Draft baskets"
          }
          className="gap-2 px-4 pt-4 pb-3"
          titleClassName="text-base"
          customIcon={
            showDeliveredBySellarFeatures || !includeStorefrontDrafts ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-600 px-1 text-xs font-semibold text-white">
                {totalItemCount}
              </span>
            ) : (
              <></>
            )
          }
        />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={BASKETS_TAB}>
        <div className="flex flex-col gap-3 p-4">
          {draftOrderListQuery.isLoading || isLoading ? (
            <SpinnerLegacy />
          ) : (
            <>
              {showDeliveredBySellarFeatures &&
                !isDeliveredBySellar &&
                sdBasketGroups.size === 0 && (
                  <DeliveredBySellarPrompt onTurnOn={turnOnDeliveredBySellar} />
                )}

              {/* SD Baskets – one consolidated card per sdBasketId */}
              {showDeliveredBySellarFeatures &&
                sortedSdBasketGroups.map(([basketId, entries], index) => {
                  const basket = baskets.find(
                    (candidate) => candidate.uuid === basketId,
                  );
                  return basket ? (
                    <ConsolidatedSdBasketCard
                      key={basketId}
                      index={index + 1}
                      sdBasketId={basketId}
                      basket={basket}
                      entries={entries}
                      onClose={() => setActive(false)}
                      isDeliveredBySellarEnabled={isDeliveredBySellar}
                      onTurnOnDeliveredBySellar={
                        continueDeliveredBySellarShopping
                      }
                      onProductClick={openProduct}
                      onSupplierClick={openSupplier}
                    />
                  ) : null;
                })}

              {/* Storefront Draft orders – read-only summary cards */}
              {storefrontDraftOrders.length > 0 && (
                <>
                  {showDeliveredBySellarFeatures && (
                    <div className="mt-2 flex w-full items-center justify-between gap-2 border-t border-zinc-300 pt-3">
                      <H6 className="flex items-center gap-2 font-semibold text-zinc-800">
                        More baskets{" "}
                        <InfoBadge
                          infoContent={{
                            title:
                              "Baskets you've built on individual supplier's storefronts are shown here",
                          }}
                        />
                      </H6>
                      <Button
                        color="transparent"
                        size="small"
                        className="normal-case"
                        onClick={() => navigate(DRAFT_ORDERS)}
                      >
                        View all <Icon icon="CHEVRON_RIGHT" size={0.8} />
                      </Button>
                    </div>
                  )}
                  {storefrontDraftOrders.map((order) => {
                    const supplierInfo = supplierStorefrontPages.find(
                      (info) => info.supplier.id === order.SupplierId,
                    );
                    if (!supplierInfo) return null;

                    return (
                      <StorefrontDraftOrderCard
                        key={order.SupplierId}
                        order={order}
                        supplierStorefrontInfo={supplierInfo}
                        onClose={() => setActive(false)}
                      />
                    );
                  })}
                </>
              )}
              {!includeStorefrontDrafts &&
                (!showDeliveredBySellarFeatures ||
                  (isDeliveredBySellar &&
                    sortedSdBasketGroups.length === 0)) && (
                  <p className="py-6 text-center text-sm text-zinc-500">
                    Your basket is empty.
                  </p>
                )}
              {!showDeliveredBySellarFeatures && includeStorefrontDrafts && (
                <Button
                  color="transparent"
                  size="small"
                  className="self-end"
                  onClick={() => navigate(DRAFT_ORDERS)}
                >
                  Go to draft baskets <Icon icon="CHEVRON_RIGHT" />
                </Button>
              )}
            </>
          )}
        </div>
      </TabbedDrawerTab>
    </TabbedDrawer>
  );
};
